import { CurrentCrewMemberType } from "@/context/CrewProvider";
import { z } from "zod";


export const yourDetailsFormSchema = z.object({
  name: z.string(),
  nick_name: z.string().nullish(),
  date_of_birth: z.date().nullish(),
  email: z.string().email("Please enter a valid email address."),
  mobile: z.string().nullish(),
  date_moved_in: z.date().nullish(),
  address_line_1: z.string().nullish(),
  address_line_2: z.string().nullish(),
  address_line_3: z.string().nullish(),
  address_postcode: z.string().nullish(),
  gender: z.string().nullish(),
  gender_pronoun: z.string().nullish(),
  gender_pronoun_other: z.string().nullish(),
  payroll_preference: z.string().nullish(),
  dietary_requirements: z.string().nullish(),
  target_pay: z.union([z.number(), z.string()]).nullish().transform(val =>
    typeof val === 'string' && val !== '' ? parseFloat(val) : val
  ),
  social_media_allowed: z.boolean().nullish(),
});

export const mapCrewForToFrontend = (data: CurrentCrewMemberType): z.infer<typeof yourDetailsFormSchema> => ({
  name: data?.name || '',
  nick_name: data?.nick_name || '',
  email: data?.email || '',
  mobile: data?.mobile || '',
  date_of_birth: data?.date_of_birth ? new Date(data.date_of_birth) : undefined,
  date_moved_in: data?.date_moved_in ? new Date(data.date_moved_in) : undefined,
  address_line_1: data?.address_line_1 || '',
  address_line_2: data?.address_line_2 || '',
  address_line_3: data?.address_line_3 || '',
  address_postcode: data?.address_postcode || '',
  gender: data?.gender || '',
  gender_pronoun: data?.gender_pronoun || '',
  gender_pronoun_other: data?.gender_pronoun_other || '',
  payroll_preference: data?.payroll_preference || '',
  dietary_requirements: data?.dietary_requirements || '',
  target_pay: data?.target_pay ? parseFloat(data.target_pay.toString()) : undefined,
  social_media_allowed: data?.social_media_allowed !== false,
});

const previous_address = z.object({
  address_line_1: z.string().nullish(),
  address_line_2: z.string().nullish(),
  address_line_3: z.string().nullish(),
  address_postcode: z.string().nullish(),
  date_moved_in: z.date().nullish(),
});

const previous_conviction = z.object({
  conviction: z.string().nullish(),
  date: z.date().nullish(),
  details: z.string().nullish(),
});

export const securityFormSchema = z.object({
  passport_number: z.string().nullish(),
  passport_country: z.string().nullish(),
  passport_city: z.string().nullish(),
  passport_issue: z.date().nullish(),
  passport_expiry: z.date().nullish(),
  bank_name: z.string().nullish(),
  bank_account_number: z.string().nullish(),
  bank_sort_code: z.string().nullish(),
  bank_account_name: z.string().nullish(),
  ni_number: z.string().nullish(),
  emergency_name: z.string().nullish(),
  emergency_phone: z.string().nullish(),
  license_number: z.string().nullish(),
  license_pass_date: z.date().nullish(),
  vehicle_registration: z.string().nullish(),
  vehicle_make: z.string().nullish(),
  vehicle_model: z.string().nullish(),
  previous_addresses: z.array(previous_address).nullish(),
  previous_convictions: z.array(previous_conviction).nullish()
});

export const documentFormSchema = z.object({
  drivers_license_number: z.string().nullish(),
  drivers_license_issue: z.date().nullish(),
  drivers_license_expiry: z.date().nullish(),
  driving_license_type: z.string().nullish(),
  driving_license_entitlement: z.string().nullish(),
  points_on_license: z.string().nullish(),
  points_details: z.string().nullish(),
  dbs_number: z.string().nullish(),
  dbs_date: z.date().nullish(),
});

export const mapCrewSecurityForToFrontend = (
  data: CurrentCrewMemberType,
  securityDetails?: { crew_previous_addresses: any[]; crew_convictions: any[] } | null,
): z.infer<typeof securityFormSchema> => ({
  passport_number: data?.passport_number || '',
  passport_country: data?.passport_country || '',
  passport_city: data?.passport_city || '',
  passport_issue: data?.passport_issue ? new Date(data.passport_issue) : undefined,
  passport_expiry: data?.passport_expiry ? new Date(data.passport_expiry) : undefined,
  bank_name: data?.bank_name || '',
  bank_account_number: data?.bank_account_number || '',
  bank_sort_code: data?.bank_sort_code || '',
  bank_account_name: data?.bank_account_name || '',
  ni_number: data?.ni_number || '',
  emergency_name: data?.emergency_name || '',
  emergency_phone: data?.emergency_phone || '',
  license_number: data?.license_number || '',
  license_pass_date: data?.license_pass_date ? new Date(data.license_pass_date) : undefined,
  vehicle_registration: data?.vehicle_registration || '',
  vehicle_make: data?.vehicle_make || '',
  vehicle_model: data?.vehicle_model || '',
  previous_addresses: securityDetails?.crew_previous_addresses ? securityDetails.crew_previous_addresses.map(addr => ({
    ...addr,
    date_moved_in: addr.date_moved_in ? new Date(addr.date_moved_in) : undefined
  })) : undefined,
  previous_convictions: securityDetails?.crew_convictions ? securityDetails.crew_convictions.map(conv => ({
    ...conv,
    date: conv.date ? new Date(conv.date) : undefined
  })) : undefined
});